import { Callout } from "components/Form/components/Callout/Callout";
import { Icon } from "components/Icon/Icon";
import { IconCollection } from "components/Icon/IconCollection";
import IconButton from "components/IconButton";
import { MaximizeHandler } from "components/MaximizeHandler/MaximizeHandler";
import { Modal } from "components/Modal/Modal";
import "components/Modal/Modal.scss";
import NotificationService from "components/Notification/Notification.service";
import RecordLabel from "components/RecordLabel";
import RecordTable from "components/Table/RecordTable";
import { getUserData } from "containers/Auth/Auth.selectors";
import { actions as dependencyVisualizationActions } from "containers/Resources/screens/ResourcesSingle/components/Visualization/DependencyVisualization/dependencyVisualization.reducer";
import {
    useReconnectingWebsocket,
    useSecondaryLiveDataHold,
} from "containers/Resources/screens/ResourcesSingle/utils";
import { wasSocketIntentionallyClosed } from "containers/Resources/screens/ResourcesSingle/socketIntentionalClose";
import { useWorkspaceConfigValue } from "containers/WorkspaceConfig/useWorkspaceConfigValue";
import { isEmpty, isEqual } from "lodash";
import QueryString from "qs";
import { Resizable } from "re-resizable";
import { MouseEventHandler, useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import { Link } from "react-router-dom";
import { useMount } from "react-use";
import ReactFlow, {
    ControlButton,
    Controls,
    Panel,
    ReactFlowProps,
    ReactFlowProvider,
} from "reactflow";
import "reactflow/dist/style.css";
import ReconnectingWebSocket from "reconnecting-websocket";
import { mapScreensToPaths } from "utils/RouterManager/utils";
import { getFullUrl, getRelativeUrl } from "utils/service";
import { capitalizeFirstLetter, useOutsideClick } from "utils/utils";
import { Direction } from "../visualization.service";
import "./DependencyVisualization.scss";
import ItemNode from "./customNodeTypes/ItemNode";
import LayoutDummyNode from "./customNodeTypes/LayoutDummyNode";
import ReturnEdgeDummyNode from "./customNodeTypes/ReturnEdgeDummyNode";
import {
    getEdges,
    getNodes,
    getResourceNodes,
    getSelectedResources,
} from "./dependencyVisualization.selectors";
import {
    ProcessEdge,
    ProcessNode,
    ProcessState,
    EdgeKind,
    edgeKindColors,
    edgeKindLabels,
    getActiveEdges,
    getEdgeKindStyle,
    getParentEdges,
    getParentNodes,
    getPositionedNodes,
    getPositionedResourceNodes,
    sortNodesByCreatedAt,
    transferOrderToIds,
} from "./dependencyVisualization.service";

interface DependencyVisualizationProps {
    hasParent: boolean;
}

const nodeTypes = {
    FLOW: ItemNode,
    SCHEDULE: ItemNode,
    CONNECTION: ItemNode,
    SCRIPT: ItemNode,
    WRAPPER: ItemNode,
    layoutDummy: LayoutDummyNode,
    returnEdgeDummy: ReturnEdgeDummyNode,
};

const CustomContextMenu = ({
    onClick,
    top,
    left,
    link,
}: {
    onClick: MouseEventHandler<HTMLDivElement>;
    top: number;
    left: number;
    link: string;
}) => {
    return (
        <div
            style={{
                top,
                left,
            }}
            onClick={onClick}
            className="customMenuPane"
        >
            <a href={link} target="_blank" rel="noopener noreferrer">
                Open in a new tab
            </a>
        </div>
    );
};

const DependencyNodesVisualization = (
    props: ReactFlowProps & {
        context: {
            setLayoutDirection: (direction: Direction) => void;
            layoutDirection: Direction;
        };
        fadingNodes?: any[];
    }
) => {
    const nodes = useSelector(getNodes);
    const edges = useSelector(getEdges);
    const selectedResources = useSelector(getSelectedResources);
    const fadingNodes = props.fadingNodes ?? [];

    const getSelectionState = (selectedResources, node): boolean | undefined => {
        if (isEmpty(selectedResources)) return undefined;
        return node.resources.some((resource) =>
            selectedResources.some((selectedResourceId) => resource.id === selectedResourceId)
        );
    };

    // carry-over previous selection state
    const effectiveNodes = [...nodes, ...fadingNodes].map((node) => {
        const selectionState = getSelectionState(selectedResources, node);
        return {
            ...node,
            selected: selectionState,
            data: {
                ...node.data,
                selected: selectionState,
            },
        };
    });

    const [menu, setMenu] = useState<any>(null);
    const ref = useRef<HTMLDivElement>(null);

    const onNodeContextMenu = useCallback(
        (event, node) => {
            // Prevent native context menu from showing
            event.preventDefault();
            const relativeUrl = getRelativeUrl("execution", node.originalId);
            const link = getFullUrl(relativeUrl);

            // Calculate position of the context menu. We want to make sure it
            // doesn't get positioned off-screen.
            const pane = ref.current?.getBoundingClientRect();
            if (pane) {
                setMenu({
                    link,
                    top: event.clientY,
                    left: event.clientX,
                });
            }
        },
        [setMenu]
    );

    const onPaneClick = useCallback(() => setMenu(null), [setMenu]);

    useOutsideClick(ref, () => {
        onPaneClick();
    });

    // Only show legend entries for edge kinds actually present in the current graph.
    const legendKinds = (["parent_child", "both", "dependency"] as EdgeKind[]).filter((kind) =>
        edges.some((edge: ProcessEdge) => edge.kind === kind)
    );

    return (
        <ReactFlow
            nodes={effectiveNodes}
            edges={edges}
            ref={ref}
            onNodeContextMenu={onNodeContextMenu}
            onPaneClick={onPaneClick}
            {...props}
        >
            {legendKinds.length > 0 && (
                <Panel position="top-right" className="dependencyVisualizationLegend">
                    {legendKinds.map((kind) => (
                        <span key={kind} className="legendEntry">
                            <span
                                className="legendSwatch"
                                style={{ backgroundColor: edgeKindColors[kind] }}
                            />
                            {edgeKindLabels[kind]}
                        </span>
                    ))}
                </Panel>
            )}
            <Controls showInteractive={false} showZoom={false}>
                <ControlButton
                    onClick={() =>
                        props.context.setLayoutDirection(
                            props.context.layoutDirection === Direction.Vertical
                                ? Direction.Horizontal
                                : Direction.Vertical
                        )
                    }
                >
                    <Icon icon={IconCollection.rotateRight} />
                </ControlButton>
            </Controls>
            {menu && <CustomContextMenu onClick={onPaneClick} {...menu} />}
        </ReactFlow>
    );
};

export const ExecutionRelatedRecordListing = () => {
    const resourceNodes = useSelector(getResourceNodes);
    const selectedResources = useSelector(getSelectedResources);
    const dispatch = useDispatch();
    const statefulResourceNodes = resourceNodes.map((resourceNode) => {
        const existingNode = selectedResources.find(
            (selectedResourceId) => selectedResourceId === resourceNode.id
        );
        if (existingNode != null)
            return {
                ...resourceNode,
                selected: true,
            };
        else return resourceNode;
    });

    const onClick = (_, id) => {
        const selection = [id];
        // deselect on click-again
        const effectiveSelection = isEqual(selection, selectedResources) ? [] : selection;

        dispatch({
            type: dependencyVisualizationActions.STORE_SELECTED_RESOURCES_NODES,
            selectedResources: effectiveSelection,
        });
    };

    const columns = [
        {
            label: "Name",
            key: "name",
            shown: true,
            sortable: true,
            dto: ({ data, id, type }) => {
                return (
                    <RecordLabel
                        key={id}
                        link={getRelativeUrl(type, id)}
                        name={data?.name}
                        iconByType={type}
                    />
                );
            },
        },
        {
            label: "Type",
            key: "type",
            shown: true,
            sortable: true,
            dto: ({ type }) => (type ? capitalizeFirstLetter(type) : null),
        },
        {
            label: "Highlight in visualization",
            key: "highlight",
            shown: true,
            sortable: false,
            dto: ({ id, selected }) => (
                <IconButton
                    icon={selected ? IconCollection.eyeOutline : IconCollection.eyeOffOutline}
                    onClick={(e) => onClick(e, id)}
                />
            ),
        },
    ];

    return (
        <RecordTable
            columns={columns}
            data={statefulResourceNodes}
            totalItemCount={statefulResourceNodes?.length}
            recordTypes="all"
            isLoading={false}
            options={{
                toolbar: "none",
                toolbarFilters: false,
                selectableRows: "none",
                showSearch: false,
                viewColumns: false,
            }}
        />
    );
};

export const DependencyVisualization = ({ hasParent }: DependencyVisualizationProps) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const queryString = QueryString.parse(window?.location?.search.substring(1));
    const recordId = queryString?.id;

    const userData = useSelector(getUserData);
    const workspaceID = userData?.workspace_id;

    const visualizationMaxNodes = useWorkspaceConfigValue("VISUALIZATION_MAX_NODES");
    const [currentNodesCount, setCurrentNodesCount] = useState(0);
    const [isWarningShown, setWarningShown] = useState(false);

    useEffect(() => {
        if (currentNodesCount >= visualizationMaxNodes) {
            setWarningShown(true);
        } else {
            setWarningShown(false);
        }
    }, [currentNodesCount, visualizationMaxNodes]);

    const [layoutDirection, setLayoutDirection] = useState<Direction>(Direction.Horizontal);
    const [fadingNodes, setFadingNodes] = useState<any[]>([]);
    const prevNodesRef = useRef<any[]>([]);

    useMount(() => {
        dispatch({ type: dependencyVisualizationActions.CLEAR_ALL });
    });

    const secondaryLiveDataHold = useSecondaryLiveDataHold();

    const { getSocket, deleteSocket } = useReconnectingWebsocket();

    useEffect(() => {
        if (secondaryLiveDataHold) {
            return;
        }
        const typedWindow: any = window;
        const websocketHost =
            typedWindow.REACT_APP_WEBSOCKET_API || `wss://${typedWindow.location.host}`;
        let socket: ReconnectingWebSocket | null = null;

        const url = `${websocketHost}${typedWindow.REACT_APP_WEBSOCKET_API_NAMESPACE}/execution/${recordId}/process/visualization`;
        if (socket) {
            deleteSocket(url);
        }

        socket = getSocket(url);
        // Surface connection failures instead of leaving the panel silently
        // empty. A stable toastId de-duplicates the reconnect storm.
        socket?.addEventListener("error", () => {
            // Skip when we tore this socket down ourselves (unmount / navigation
            // / tab-hidden) -- not a real live-data failure.
            if (wasSocketIntentionallyClosed(socket)) {
                return;
            }
            // Suppressed while the primary `/ui` socket is down (the top banner
            // already signals the outage); fires only for an isolated failure
            // while otherwise connected.
            NotificationService.liveDataError(
                "Could not load the dependency visualization. Check your connection or refresh the page.",
                { toastId: "dependency-visualization-error" }
            );
        });
        socket?.addEventListener("message", (event) => {
            const processState: ProcessState = JSON.parse(event.data);

            setCurrentNodesCount(processState.nodes.length);

            const sortedNodes = sortNodesByCreatedAt(processState.nodes, processState.edges);
            const [idOrderedNodes, idOrderedEdges] = transferOrderToIds(
                sortedNodes,
                processState.edges
            );

            const positionedNodes = getPositionedNodes(
                idOrderedNodes,
                idOrderedEdges,
                layoutDirection
            );

            const parentNodes = getParentNodes(hasParent, processState.nodes, processState.edges);

            const positionedInfoEnrichedNodes = positionedNodes.map((positionedNode) => {
                return {
                    ...positionedNode,
                    data: {
                        ...positionedNode.data,
                        isParentNode: parentNodes.some(
                            (parentNode) =>
                                parentNode.id ===
                                (positionedNode as unknown as ProcessNode).originalId // TODO fix typing (requires reactflow v11 to get this done properly https://app.clickup.com/t/861mhppyc)
                        ),
                        isCurrentExecution:
                            recordId === (positionedNode as unknown as ProcessNode).originalId, // TODO fix typing (requires reactflow v11 to get this done properly https://app.clickup.com/t/861mhppyc)
                    },
                };
            });

            const newNodeIds = new Set(
                positionedInfoEnrichedNodes.map(
                    (node) => (node as unknown as ProcessNode).originalId
                )
            );
            const removedNodes = prevNodesRef.current.filter(
                (node) => !newNodeIds.has(node.originalId)
            );
            if (removedNodes.length > 0) {
                const fadingSnapshot = removedNodes.map((node) => ({
                    ...node,
                    data: {
                        ...node.data,
                        fading_out: false,
                    },
                }));
                setFadingNodes((prev) => [
                    ...prev.filter((node) => newNodeIds.has(node.originalId)),
                    ...fadingSnapshot,
                ]);
                requestAnimationFrame(() => {
                    setFadingNodes((prev) =>
                        prev.map((node) =>
                            fadingSnapshot.some(
                                (fadingNode) => fadingNode.originalId === node.originalId
                            )
                                ? {
                                      ...node,
                                      data: {
                                          ...node.data,
                                          fading_out: true,
                                      },
                                  }
                                : node
                        )
                    );
                });
                window.setTimeout(() => {
                    setFadingNodes((prev) =>
                        prev.filter(
                            (node) =>
                                !fadingSnapshot.some(
                                    (fadingNode) => fadingNode.originalId === node.originalId
                                )
                        )
                    );
                }, 500);
            }
            prevNodesRef.current = positionedInfoEnrichedNodes;

            const parentEdges = getParentEdges(hasParent, parentNodes, processState.edges);
            const activeEdges = getActiveEdges(processState.nodes, processState.edges);

            const deletedNodeIds = new Set(
                positionedInfoEnrichedNodes
                    .filter((node) => node.data?.is_deleted)
                    .map((node) => node.id)
            );

            const transformedEdges = idOrderedEdges.map((edge) => {
                edge = { type: "default", ...edge };
                // Base stroke colour encodes the relationship KIND (parent/child vs
                // dependency vs both). Kept orthogonal to `animated`, which stays the
                // "not-yet-ended / active" cue. Later opacity filters are spread so they
                // never clobber the kind stroke.
                edge.style = { ...getEdgeKindStyle(edge.kind), ...edge.style };
                if (edge.targetHandle === "waiter") {
                    edge.target = `${edge.target}-returnDummy-${edge.source}`;
                }
                if (parentEdges.some((parentEdge) => parentEdge.id === edge.id)) {
                    // this is a parent edge
                    edge.style = { ...edge.style, filter: "opacity(50%)" };
                }
                if (deletedNodeIds.has(edge.source) || deletedNodeIds.has(edge.target)) {
                    edge.style = { ...edge.style, filter: "opacity(45%)" };
                }
                if (activeEdges.some((activeEdge) => activeEdge.id === edge.id)) {
                    edge.animated = true;
                }
                return edge;
            });

            // resource nodes
            const positionedResourceNodes = getPositionedResourceNodes(processState.resources);

            // redux
            dispatch({
                type: dependencyVisualizationActions.STORE_DEPENDENCY_VISUALIZATION_DATA,
                processState: {
                    nodes: positionedInfoEnrichedNodes,
                    edges: transformedEdges,
                    resources: positionedResourceNodes,
                },
            });
        });

        return () => {
            deleteSocket(url);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recordId, layoutDirection, dispatch, hasParent, secondaryLiveDataHold]);

    const [isMaximized, setIsMaximized] = useState(false);
    const maximize = () => {
        setIsMaximized(true);
    };

    const dependencyVisualizationComponent = (
        <ReactFlowProvider>
            <div className="reactflow-wrapper">
                <DependencyNodesVisualization
                    nodesConnectable={false}
                    nodeTypes={nodeTypes}
                    fitView
                    fitViewOptions={{ padding: 0.3, maxZoom: 0.7 }}
                    minZoom={0.1}
                    nodesDraggable={false}
                    elementsSelectable={true}
                    fadingNodes={fadingNodes}
                    onNodeClick={(event, node: any) => {
                        const relativeUrl = getRelativeUrl("execution", node.originalId);
                        const absoluteUrl = getFullUrl(relativeUrl);
                        if (event.ctrlKey) {
                            window.open(absoluteUrl, "_blank", "noopener,noreferrer");
                        } else {
                            history.push(relativeUrl);
                        }
                    }}
                    context={{
                        layoutDirection: layoutDirection,
                        setLayoutDirection: setLayoutDirection,
                    }}
                />
            </div>
        </ReactFlowProvider>
    );

    return (
        <div className="dependencyVisualization">
            {isMaximized ? (
                <Modal
                    centered={true}
                    width="100vw"
                    destroyOnClose
                    closeIcon={<Icon icon={IconCollection.arrowCollapse} />}
                    className={"visualCodeEditorModal"}
                    replaceDefaultStyles={true}
                    keyboard={true}
                    isVisible={isMaximized}
                    onSubmit={() => setIsMaximized(false)}
                    onCancel={() => setIsMaximized(false)}
                >
                    <div className="dependencyVisualization dependencyVisualizationWrapper">
                        <Resizable
                            defaultSize={{
                                width: "100%",
                                height: "100%",
                            }}
                            minHeight={226}
                            className="outerResizeContainer"
                            enable={{
                                top: false,
                                right: false,
                                bottom: false,
                                left: false,
                                topRight: false,
                                bottomRight: false,
                                bottomLeft: false,
                                topLeft: false,
                            }}
                        >
                            {dependencyVisualizationComponent}
                        </Resizable>
                    </div>
                </Modal>
            ) : (
                <>
                    <Resizable
                        defaultSize={{
                            width: "100%",
                            height: 226,
                        }}
                        minHeight={226}
                        className="outerResizeContainer"
                        handleClasses={{ bottom: "bottomHandle" }}
                        enable={{
                            top: false,
                            right: false,
                            bottom: true,
                            left: false,
                            topRight: false,
                            bottomRight: false,
                            bottomLeft: false,
                            topLeft: false,
                        }}
                    >
                        {dependencyVisualizationComponent}
                    </Resizable>
                    <MaximizeHandler maximize={maximize} />
                </>
            )}
            {isWarningShown && (
                <div className="warningContainer">
                    <Callout
                        text={
                            <div>
                                Only the first {visualizationMaxNodes} executions are displayed. See
                                VISUALIZATION_MAX_NODES in{" "}
                                <Link to={mapScreensToPaths.workspaceSettings(workspaceID)}>
                                    workspace settings
                                </Link>
                                .
                            </div>
                        }
                        type={"warning"}
                    />
                </div>
            )}
        </div>
    );
};
